import { useTranslation } from '@silospot/i18n/react';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { Badge, Btn, Card, CardHead } from './kit';

/**
 * Settings → Integrations → Cloudflare. A compact summary that links to the full
 * connect/manage page (#cloudflare). KPIs come from the real connection status.
 */
export function CloudflareCard({ projectId }: { projectId: string }): ReactElement {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const statusQ = useQuery({
    queryKey: ['cf-status', projectId],
    queryFn: () => api.cfStatus(projectId),
  });
  const s = statusQ.data;
  const go = () => navigate(`/projects/${projectId}/cloudflare`);

  return (
    <Card className="lg:col-span-2">
      <CardHead title={t('cfcard.title')} sub={t('cfcard.sub')} />
      {!s ? (
        <div className="h-10 animate-pulse rounded bg-neutral-bg" />
      ) : !s.connected ? (
        <div className="flex flex-wrap items-center gap-3">
          <Btn primary onClick={go}>
            {t('cfcard.connect')}
          </Btn>
          <span className="text-[12px] text-text-3">{t('cfcard.tagline')}</span>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-bg/40 p-3.5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Badge tone="clean" dot>
                  {t('cfcard.active')}
                </Badge>
                <span className="font-mono text-[12px] text-text-3">{s.zoneName}</span>
              </div>
              <div className="mt-1 text-[12px] text-text-3">
                {s.proxied ? t('cfcard.proxyOn') : t('cfcard.proxyOff')}
              </div>
            </div>
            <Btn onClick={go}>{t('cfcard.manage')}</Btn>
          </div>
        </div>
      )}
    </Card>
  );
}
